/**
 * Pure mapping from the four existing report channels to the Overview's cards
 * — Card P145.
 *
 * ⛔⛔ PURE ON PURPOSE. Every rule on this screen that a test needs to attack
 * — the delta arithmetic, which cards are tracked, how a System Admin row is
 * labelled — lives here, as functions over plain objects. The component
 * renders what these return. A rule embedded in JSX is a rule no arm can
 * reach without a DOM.
 */

/**
 * ⛔⛔ THE DELTA, AND ITS DIVIDE-BY-ZERO.
 *
 * Growth from nothing is not a percentage. 0 → 40 is not "+Infinity%" and it
 * is certainly not "+0%"; it is a quantity appearing where there was none, and
 * the honest readout is no chip at all.
 *
 * ⚠ Both-zero is also NOT 0% change — it is "no activity either period", and
 * painting a neutral ■ 0.0% over it implies a measurement that did not happen.
 */
export function deltaPct(current, previous) {
  /*
   * ⛔⛔ TYPEOF FIRST, AND THIS WAS A REAL BUG CAUGHT BY ITS OWN ARM.
   * `Number(null)` is 0 and `Number(null)` is FINITE, so a missing current
   * value sailed past the isFinite guard and rendered as "▼ 100.0% vs
   * previous" — an absent measurement presented as a total collapse, which
   * is exactly the false-zero failure this screen exists to end.
   * `Number([])` is 0 too; `Number({})` is NaN. Only real numbers proceed.
   */
  if (typeof current !== 'number' || typeof previous !== 'number') return null;
  const c = current;
  const p = previous;
  if (!Number.isFinite(c) || !Number.isFinite(p)) return null;
  if (p === 0) return null;
  return ((c - p) / Math.abs(p)) * 100;
}

/**
 * ⭐⭐ THE SYSTEM ADMIN IS NOT A REP — measured 28 Oct: 1,082 overdue
 * follow-ups sit on that account.
 *
 * Listed as a person it is the worst performer on the team by a factor of
 * fifty, and it is not a person. Listed as "Unassigned" it is what it
 * actually is: work no one owns, which is a real finding and a different
 * conversation from a rep being behind.
 *
 * ⚠ Matched on the ACCOUNT, not on a display-name substring: a staff member
 * legitimately called "Admin" must not be folded into this bucket.
 */
export const SYSTEM_ADMIN_USERNAMES = Object.freeze(["system", "sysadmin", "system_admin", "systemadmin"]);
export const UNASSIGNED_LABEL = "Unassigned (System Admin)";

/**
 * ⚠⚠ THE SYSTEM ACCOUNT IS ALSO RECOGNISED BY ITS DISPLAY NAME, because the
 * only place the overdue rule exposes a rep is inside a SENTENCE — see
 * mergeRepRows. There is no username on that path, so an account match alone
 * cannot see it.
 *
 * ⛔ Exact, case-insensitive, whole-string — never a substring. "System
 * Administrator" is the account; "Adminah Okafor" is a colleague, and a
 * contains-test would rename her out of her own row.
 */
const SYSTEM_ADMIN_NAMES = Object.freeze([
  "system administrator", "system admin", "systemadmin", "system",
]);

export function repLabel(row = {}) {
  const uname = String(row.username || "").trim().toLowerCase();
  const dname = String(row.rep || row.ownerName || row.displayName || "").trim().toLowerCase();
  if (row.repId === null || row.repId === undefined) return UNASSIGNED_LABEL;
  if (SYSTEM_ADMIN_USERNAMES.includes(uname)) return UNASSIGNED_LABEL;
  if (SYSTEM_ADMIN_NAMES.includes(dname)) return UNASSIGNED_LABEL;
  /*
   * ⛔⛔ `ownerName` IS THE FIELD, and omitting it is what put "Rep 14" on a
   * live executive screen. routes/_rep_performance.js:125 returns `ownerName`;
   * this read `rep || displayName` and fell through to the id for every row.
   * A placeholder that looks like data is worse than a blank.
   */
  return row.rep || row.ownerName || row.displayName || `Rep ${row.repId}`;
}

export function isUnassignedRow(row = {}) {
  return repLabel(row) === UNASSIGNED_LABEL;
}

/**
 * Merge the two per-rep sources the repo already has. They are separate
 * builders on separate channels (won $ + conversion from rep-performance;
 * overdue follow-ups from the attention rules), and this card composes them
 * rather than adding a third query to the server — which is W3's file.
 */
/**
 * ⛔⛔ THE OVERDUE COUNTS LIVE IN `attention.items`, NOT IN A LIST OF THEIR OWN.
 *
 * MEASURED ON v2.1.10: the Team panel showed every rep at 0 while Exceptions,
 * two panels away, showed 1,082 / 202 / 72 / 63. This read
 * `attention.overdueByRep`, which does not exist — `routes/_ceo_attention.js`
 * emits one ITEM per rep under `ruleKey: "overdue_follow_ups"`. The lookup
 * returned undefined, every rep got 0, and a panel of zeros is indistinguishable
 * from a team with nothing overdue.
 *
 * ⚠ The rep's NAME is only in the item's title sentence
 * ("202 overdue follow-ups — Manny Razo"); the id is in
 * `link.filter.assignedTo`. So the id is the join key and the name is parsed
 * off the sentence — and ONLY as a fallback, because the performance row's
 * `ownerName` is the authoritative spelling when the rep appears in both.
 */
export function overdueRowsFromAttention(attention) {
  const items = (attention && Array.isArray(attention.items) ? attention.items : [])
    .filter((it) => it && it.ruleKey === "overdue_follow_ups");
  return items.map((it) => {
    const id = it.link && it.link.filter ? it.link.filter.assignedTo : null;
    // "202 overdue follow-ups — Manny Razo" → "Manny Razo"
    const m = /—\s*(.+)$/.exec(String(it.title || ""));
    return {
      repId: id === undefined ? null : id,
      rep: m ? m[1].trim() : null,
      n: Number(it.count) || 0,
    };
  });
}

export function mergeRepRows({ performance = [], overdue = [] } = {}) {
  const byId = new Map();
  for (const r of performance) {
    const id = r.ownerId ?? r.owner_id ?? r.repId ?? null;
    byId.set(String(id), {
      repId: id, rep: r.ownerName || r.rep || r.displayName || r.display_name,
      username: r.username, wonCents: r.revenueCents ?? null,
      wonDollars: r.revenue ?? null, conversionPct: r.leadToDeposit ?? null,
      overdue: 0,
    });
  }
  for (const o of overdue) {
    const id = o.repId ?? o.rep_id ?? null;
    const k = String(id);
    const prev = byId.get(k) || { repId: id, rep: o.rep, username: o.username, wonCents: null, wonDollars: null, conversionPct: null, overdue: 0 };
    prev.overdue = Number(o.n) || 0;
    // ⚠ Only fills a MISSING name: the performance row's ownerName wins.
    if (!prev.rep && o.rep) prev.rep = o.rep;
    byId.set(k, prev);
  }
  const mapped = [...byId.values()].map((r) => ({ ...r, label: repLabel(r), unassigned: isUnassignedRow(r) }));

  /*
   * ⛔⛔ COLLAPSE BY LABEL — the duplicate "Unassigned (System Admin)" on
   * v2.1.12, one row at 1,082 and another at 0.
   *
   * TWO DIFFERENT IDS produce that one label: the System Administrator
   * account (which has a performance row and no overdue item of its own
   * under that id) and a NULL owner. Keying by id is right for joining and
   * wrong for displaying — the whole point of the label is that these are
   * the same thing to a reader, so showing it twice contradicts the label.
   *
   * ⚠ Overdue counts are SUMMED, not overwritten: two buckets of unowned
   * work are more unowned work, and taking the larger would silently drop
   * the rest. Won/conversion take the first non-null, since averaging two
   * reps' rates would invent a figure neither of them has.
   */
  const byLabel = new Map();
  for (const r of mapped) {
    const prev = byLabel.get(r.label);
    if (!prev) { byLabel.set(r.label, { ...r }); continue; }
    prev.overdue = (prev.overdue || 0) + (r.overdue || 0);
    if (prev.wonDollars == null) prev.wonDollars = r.wonDollars;
    if (prev.conversionPct == null) prev.conversionPct = r.conversionPct;
  }
  const rows = [...byLabel.values()];
  /*
   * ⚠ Unassigned sorts LAST regardless of size. At 1,082 it would otherwise
   * head the chart and visually become "the team's problem rep", which is
   * exactly the misreading this label exists to prevent.
   */
  rows.sort((a, b) => {
    if (a.unassigned !== b.unassigned) return a.unassigned ? 1 : -1;
    return (b.overdue || 0) - (a.overdue || 0);
  });
  return rows;
}

/**
 * The finance cards' state, derived from W2's summary (#727).
 *
 * ⚠ `tracked:false` is a REASON, not an absence. The builder returns why, and
 * the screen repeats it verbatim rather than inventing softer wording.
 */
/**
 * Merge the configured revenue targets into the Collected 12-month series.
 *
 * ⛔⛔ UNITS ARE THE WHOLE JOB — Card P217 (B). `targets` is keyed by month
 * and holds DOLLARS: the deleted "Revenue over time" chart plotted dollars and
 * converted its own series with centsToDollars(). The Collected card plots
 * `collectedCents`, in CENTS. Moving the target line across without
 * multiplying by 100 draws it at one hundredth of its real size — a perfectly
 * plausible dashed line, below every month, that nobody would question.
 *
 * ⚠ A month with no configured target gets NO key, so recharts leaves a gap
 * rather than connecting through it. Back-filling would invent a target for a
 * month nobody set one for, which is the rule the deleted chart carried.
 *
 * ⚠ A non-numeric target is IGNORED, not coerced: Number("abc") is NaN, and a
 * NaN in a series renders as a broken line rather than as an error.
 *
 * @param {Array<{month:string, collectedCents:number}>|null} trend
 * @param {Object<string, number>|null} targets month → DOLLARS
 * @returns {Array|null} the series, each row optionally carrying targetCents
 */
/**
 * The Collected tile's sub-line: "of which affiliate-referred $X (Y%)".
 *
 * ⛔⛔ NUMERATOR AND DENOMINATOR COME FROM ONE QUERY — Card P220 Part A.
 * Both are read off the finance period envelope, which computes them in a
 * single pass over ghl_payments for one window. Taking the dollars from the
 * affiliate channel and the total from here would be two queries over two
 * windows, and the percentage would eventually contradict the tile above it.
 *
 * ⚠ RETURNS null RATHER THAN A ZERO. "of which affiliate-referred $0.00
 * (0%)" is a claim that the affiliate channel earned nothing this period;
 * absent data must not make that claim. Null when the slice is untracked, or
 * when Collected itself is not a usable denominator.
 *
 * ⚠ A percentage is only offered when collected > 0. Dividing by zero (or by
 * a negative, which a refund-heavy month can produce) yields Infinity or a
 * nonsense sign; the amount is still shown, without a share.
 *
 * @returns {string|null}
 */
export function affiliateSubLine(summary, formatCentsFn) {
  const period = summary && summary.period;
  const slice = period && period.collectedAffiliate;
  if (!slice || slice.tracked === false || slice.value == null) return null;

  const collected = period.collected;
  const total = collected && collected.tracked !== false ? Number(collected.value) : null;
  const amount = Number(slice.value);
  if (!Number.isFinite(amount)) return null;

  const money = formatCentsFn(amount);
  if (!Number.isFinite(total) || total <= 0) return `of which affiliate-referred ${money}`;
  const pct = Math.round((amount / total) * 1000) / 10;
  return `of which affiliate-referred ${money} (${pct}%)`;
}

export function withTargetCents(trend, targets) {
  if (!Array.isArray(trend)) return null;
  if (!targets || typeof targets !== 'object') return trend;
  return trend.map((r) => {
    const t = Number(targets[r.month]);
    return Number.isFinite(t) ? { ...r, targetCents: Math.round(t * 100) } : r;
  });
}

export function financeCards(summary, { loading = false, failed = false } = {}) {
  if (failed) return { state: 'failed', reason: 'The finance summary could not be read.' };
  if (loading || !summary) return { state: 'loading' };
  if (summary.tracked === false) {
    return { state: 'untracked', reason: summary.reason || summary.message || 'No payments have been mirrored yet.' };
  }
  return { state: 'ok', summary };
}
